import React, { useEffect, useMemo, useState } from "react";
import { Trans, t } from "@lingui/macro";
import { HiOutlineArrowSmRight, HiInformationCircle } from "react-icons/hi";
import { Link } from "react-router-dom";
import { ethers } from "ethers";
import { useWeb3React } from "@web3-react/core";
import useSWR from "swr";
import { approveTokens, useInfoTokens } from "domain/tokens";
import { getTokenInfo } from "domain/tokens/utils";
import "./StakeV3.scss";
import Modal from "components/Modal/Modal";
import PalmToken from "img/PalmToken.svg";
import Button from "components/Button/Button";
import "./Stake.css";
import { getContractAddress } from "config/contracts";
import PalmRewardRouter from "abis/PalmRewardRouter.json";
import Token from "abis/common/Token.json";
import { useChainId } from "lib/chains";
import { getChainName, IS_NETWORK_DISABLED } from "config/chains";
import { bigNumberify, expandDecimals, formatAmount, parseValue } from "lib/numbers";
import { callContract, contractFetcher } from "lib/contracts";
import { PLACEHOLDER_ACCOUNT } from "lib/legacy";
import { useTokenPrices } from "lib/api/useTokenPrices";
import { parseUnits } from "@ethersproject/units";
import { PALM_DECIMALS, USD_DECIMALS } from "lib/constants";
import { usePlpUnstakeCooldown } from "lib/usePlpUnstakeCooldown";
import formatDistanceStrict from "date-fns/formatDistanceStrict";

export const STAKE_ACTION = "STAKE_ACTION";
export const UNSTAKE_ACTION = "UNSTAKE_ACTION";
export const PALM_TOKEN = "PALM_TOKEN";
export const PLP_TOKEN = "PLP_TOKEN";

const fractions = [
  {
    value: 0.1,
    label: "10%",
  },
  {
    value: 0.25,
    label: "25%",
  },
  {
    value: 0.5,
    label: "50%",
  },
  {
    value: 0.75,
    label: "75%",
  },
  {
    value: 1,
    label: "100%",
  },
];

const StakeModal = ({ open, setOpen, palmBalance, palmStaked, stakeModalData, connectWallet, setPendingTxns }) => {
  const [hideModal, setHideModal] = useState(JSON.parse(localStorage.getItem("doNotShowModalForBuyingPLP")) || false);
  const [palmValue, setPalmValue] = useState("");
  const [fraction, setFraction] = useState();
  const { price: palmPrice } = useTokenPrices("PALM");
  const [annualRoi] = useState(0);
  const [isApproving, setIsApproving] = useState(false);
  const [isWaitingForApproval, setIsWaitingForApproval] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { provider, isActive, account } = useWeb3React();
  const { chainId } = useChainId();

  const { action, token } = stakeModalData;
  const isStaking = action === STAKE_ACTION;
  const tokenName = token === PLP_TOKEN ? "PLP" : "PALM";
  const actionName = isStaking ? t`Stake` : t`Unstake`;
  const stakeLink = "/buy";
  const balanceLabel = isStaking ? t`Balance` : t`Palm Staked Balance`;
  const balanceFormatted = isStaking
    ? formatAmount(palmBalance, PALM_DECIMALS, 2, false)
    : formatAmount(palmStaked, PALM_DECIMALS, 2, false);

  const plpUnstakeCooldown = usePlpUnstakeCooldown();
  const plpUnstakeCooldownText = formatDistanceStrict(plpUnstakeCooldown, 0, { roundingMethod: "ceil" });

  const plpTokenInfoText = isStaking
    ? t`To stake PLP tokens, you need to buy them on the Buy page. Once you buy PLP, they will be automatically added into the staking pool and earn 50% of the trading fees generated by the Palmswap Exchange. You can unstake the PLP tokens at any time. After a cooldown period of ${plpUnstakeCooldownText}, you can sell your PLP tokens.`
    : t`To unstake PLP tokens, you need to unstake them on the Buy page. After a cooldown period of ${plpUnstakeCooldownText}, you can sell your PLP tokens. The rewards you have received so far can be claimed even after you have unstaked PLP, but you will not receive any new rewards.`;

  const palmAmount = parseValue(palmValue, PALM_DECIMALS) || bigNumberify(0);

  const amountInUsd = useMemo(
    () => parseUnits(String(palmPrice), USD_DECIMALS).mul(palmAmount).div(expandDecimals(1, PALM_DECIMALS)),
    [palmPrice, palmAmount]
  );

  const palmTokenAddress = getContractAddress(chainId, "PALM");
  const palmRewardRouterAddress = getContractAddress(chainId, "PalmRewardRouter");
  const feePalmTrackerAddress = getContractAddress(chainId, "FeePalmTracker");
  const { data: tokenAllowance, mutate: refetchTokenAllowance } = useSWR(
    [isActive, chainId, palmTokenAddress, "allowance", account || PLACEHOLDER_ACCOUNT, feePalmTrackerAddress],
    contractFetcher(provider, Token),
    {
      refreshInterval: 3000,
    }
  );

  // We don't need token allowance check, if modal is in Unstake mode
  const needApproval = isStaking && tokenAllowance && palmAmount && palmAmount.gt(tokenAllowance);

  const getError = () => {
    if (IS_NETWORK_DISABLED[chainId]) {
      if (isStaking) return [t`PALM stake disabled, pending ${getChainName(chainId)} upgrade`];
      return [t`PALM unstake disabled, pending ${getChainName(chainId)} upgrade`];
    }

    if (!palmAmount || palmAmount.eq(0)) {
      return [t`Enter an amount`];
    }

    if (isStaking) {
      if (palmBalance && palmAmount && palmAmount.gt(palmBalance)) {
        return [t`Insufficient PALM balance`];
      }
    }

    return [false];
  };

  useEffect(() => {
    if (fraction && isStaking) {
      const balance = palmBalance.div(expandDecimals(1, PALM_DECIMALS));
      setPalmValue(Number(balance) * fraction);
    }
    if (fraction && !isStaking) {
      const balance = palmStaked.div(expandDecimals(1, PALM_DECIMALS));
      setPalmValue(Number(balance) * fraction);
    }
  }, [fraction, isStaking, palmBalance, palmStaked]);

  const { infoTokens } = useInfoTokens();

  const approveToken = () => {
    approveTokens({
      setIsApproving: setIsWaitingForApproval,
      library: provider,
      tokenAddress: palmTokenAddress,
      spender: feePalmTrackerAddress,
      chainId: chainId,
      onApproveSubmitted: () => {
        setIsApproving(true);
      },
      setPendingTxns,
      infoTokens,
      getTokenInfo,
      txnSuccessCallback: async () => {
        await refetchTokenAllowance();
        setIsApproving(false);
      },
    });
  };

  useEffect(() => {
    if (hideModal) {
      localStorage.setItem("doNotShowModalForBuyingPLP", JSON.stringify(hideModal));
    } else {
      localStorage.setItem("doNotShowModalForBuyingPLP", JSON.stringify(hideModal));
    }
  }, [hideModal]);

  const closeModal = () => {
    setPalmValue("");
    setOpen(!open);
  };

  const stakePalm = () => {
    setIsSubmitting(true);

    const contract = new ethers.Contract(palmRewardRouterAddress, PalmRewardRouter.abi, provider.getSigner());
    const method = "stakePalm";
    const params = [palmAmount];

    callContract(chainId, contract, method, params, {
      sentMsg: t`Stake submitted.`,
      failMsg: t`Stake failed.`,
      successMsg: `${palmValue} ` + t`PALM staked`,
      setPendingTxns,
      txnSuccessCallback: () => {
        setIsSubmitting(false);
        closeModal();
      },
    }).catch(() => {
      setIsSubmitting(false);
    });
  };

  const unstakePalm = () => {
    setIsSubmitting(true);

    const contract = new ethers.Contract(palmRewardRouterAddress, PalmRewardRouter.abi, provider.getSigner());
    const method = "unstakePalm";
    const params = [palmAmount];

    callContract(chainId, contract, method, params, {
      sentMsg: t`Unstake submitted.`,
      failMsg: t`Unstake failed.`,
      successMsg: `${palmValue} ` + t`PALM unstaked`,
      setPendingTxns,
      gasPreference: "aggressive",
      txnSuccessCallback: () => {
        setIsSubmitting(false);
        setPalmValue("");
      },
    }).catch(() => {
      setIsSubmitting(false);
    });
  };

  const onClickPrimary = () => {
    if (!isActive) {
      connectWallet();
      return;
    }

    if (needApproval) {
      approveToken();
      return;
    }

    if (isStaking) {
      stakePalm();
    } else {
      unstakePalm();
    }
  };

  const getPrimaryText = () => {
    if (!isActive) {
      return t`Connect Wallet`;
    }
    const [error, modal] = getError();
    if (error && !modal) {
      return error;
    }

    if (needApproval && isWaitingForApproval) {
      return t`Waiting for Approval`;
    }
    if (isApproving) {
      return t`Approving PALM...`;
    }
    if (needApproval) {
      return t`Approve PALM`;
    }

    if (isSubmitting) {
      return isStaking ? t`Stake...` : t`Unstake...`;
    }

    return actionName;
  };

  return (
    <Modal
      className="Connect-wallet-modal tailwind"
      isVisible={open}
      setIsVisible={closeModal}
      label={`${actionName} ${tokenName}`}
    >
      {token === PALM_TOKEN ? (
        <div className="Earn-modal">
          <div className="Stake-modal-top-section">
            <div className="inactive-text">Amount</div>
            <div className="input-wrapper">
              <input
                type={"number"}
                className="Earn-input"
                value={palmValue}
                onChange={(e) => {
                  setFraction(undefined);
                  setPalmValue(e.target.value);
                }}
              />
              <div className="input-end-content">
                <div className="inactive-text">~ ${formatAmount(amountInUsd, USD_DECIMALS, 2)}</div>
              </div>
            </div>

            <div className="inactive-text">
              {balanceLabel} {balanceFormatted} PALM
            </div>
            <div className="fraction-selector-wrapper">
              {fractions.map((fraction, i) => (
                <div className="fraction-selector-item" key={i} onClick={() => setFraction(fraction.value)}>
                  {fraction.label}
                </div>
              ))}
            </div>
          </div>
          <div className="Stake-modal-bottom-section">
            {action === STAKE_ACTION && (
              <>
                <div className="label-value">
                  <div className="inactive-text">
                    <Trans>Estimated annual ROI</Trans>
                  </div>
                  <div className="inactive-text">{annualRoi} USDT</div>
                </div>
                <a
                  className="Modal-info-card"
                  rel="noopener noreferrer"
                  href="https://pancakeswap.finance/swap?outputCurrency=0x29745314B4D294B7C77cDB411B8AAa95923aae38"
                >
                  <img src={PalmToken} alt="token" className="Token-icon" />
                  <div className="info-block popup-infoblock">
                    <div className="white-text">
                      <Trans>Get tokens on Pancakeswap</Trans>
                    </div>
                    <div className="inactive-text">PALM</div>
                  </div>
                  <HiOutlineArrowSmRight className="right-arrow" />
                </a>
              </>
            )}
            <Button
              variant="primary-action"
              disabled={isWaitingForApproval || isApproving || isSubmitting}
              onClick={onClickPrimary}
              className="w-full rounded mt-1"
            >
              {getPrimaryText()}
            </Button>
          </div>
        </div>
      ) : (
        <div className="Earn-modal">
          <div className="Stake-modal-top-section">
            <div className="Stake-modal-info">
              <div>
                <HiInformationCircle className="inactive-text my-[.4rem]" />
              </div>
              <div className="text-inactive text-[1.4rem] leading-[2.4rem]">{plpTokenInfoText}</div>
            </div>
          </div>

          {!isStaking && (
            <dl className="mt-[2rem] text-inactive text-[1.4rem] leading-[2.4rem] flex justify-between items-center">
              <dt>
                <Trans>Unstake cooldown</Trans>
              </dt>

              <dd>{plpUnstakeCooldownText}</dd>
            </dl>
          )}

          <div className="flex items-center mt-[2rem] mb-[3rem]">
            <input
              type="checkbox"
              className="Stake-checkbox"
              id="Stake-checkbox"
              checked={hideModal}
              onChange={() => setHideModal(!hideModal)}
            />
            <label htmlFor="Stake-checkbox">
              <span className="ml-[0.8rem] isolate tailwind text-inactive text-[1.4rem whitespace-nowrap">
                <Trans>Don’t show this message again</Trans>
              </span>
            </label>
          </div>
          <Button variant="primary-action" rounded className="w-full rounded">
            <Link className="w-full" to={stakeLink}>
              <Trans>Visit "Buy" page</Trans>
            </Link>
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default StakeModal;
